import { useState, useEffect } from 'react';
import { FaLinkedin, FaThumbsUp, FaThumbsDown, FaComment } from 'react-icons/fa';
import { RiDownloadLine } from "react-icons/ri";
import { SiSalesforce } from 'react-icons/si';
import { LuExternalLink } from "react-icons/lu";
import ReactMarkdown from 'react-markdown';
import { supabase } from '../supabaseClient';
import '../styles/DashboardSearches.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Feedback Modal Component
function FeedbackModal({ isOpen, onClose, onSubmit, feedbackType, companyName, isSubmitting, existingDetails }) {
  const [details, setDetails] = useState(existingDetails || '');

  useEffect(() => {
    setDetails(existingDetails || '');
  }, [existingDetails, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(details);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="feedback-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <div className={`feedback-modal-icon ${feedbackType}`}>
            {feedbackType === 'thumbs-up' ? <FaThumbsUp size={18} /> : <FaThumbsDown size={18} />}
          </div>
          <div className="feedback-modal-title">
            <h3>{feedbackType === 'thumbs-up' ? 'Good Lead' : 'Not a Good Lead'}</h3>
            <p>{companyName}</p>
          </div>
        </div>
        <div className="feedback-modal-body">
          <label htmlFor="feedback-details">
            {feedbackType === 'thumbs-up'
              ? 'What makes this a good lead? (optional)'
              : 'Why is this not a good fit? (optional)'}
          </label>
          <textarea
            id="feedback-details"
            className="feedback-details-input"
            placeholder={feedbackType === 'thumbs-up'
              ? 'e.g., Great product-market fit, strong founding team...'
              : 'e.g., Wrong industry, too early stage, already in pipeline...'}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            autoFocus
          />
        </div>
        <div className="feedback-modal-actions">
          <button className="feedback-modal-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`feedback-modal-btn submit ${feedbackType}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to check if a LinkedIn URL is valid
const isValidLinkedInUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return lower.startsWith('http') && lower.includes('linkedin.com');
};

// Helper to check if a URL is valid
const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.toLowerCase().startsWith('http');
};

// Helper to highlight matching text
const highlightText = (text, query) => {
  if (!query || !text) return text;

  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={index} className="search-highlight">{part}</mark>
    ) : part
  );
};

// Helper to convert array-or-string to string (for CSV export and search)
// Handles both old array format and new markdown string format
const arrayToString = (value, separator = ' ') => {
  if (!value) return '';
  if (Array.isArray(value)) return value.join(separator);
  // For markdown strings, strip the bullet points for plain text
  return value.replace(/^- /gm, '').replace(/\n/g, separator);
};

// Check if a string contains markdown bullets
const isMarkdownBullets = (value) => {
  if (!value || typeof value !== 'string') return false;
  return value.startsWith('- ');
};

// ReactMarkdown components config - only allows bullet lists, strips other markdown
const markdownComponents = {
  ul: ({children}) => <ul className="bullet-list">{children}</ul>,
  li: ({children}) => <li>{children}</li>,
  p: ({children}) => <>{children}</>,
  a: ({children}) => <>{children}</>,
  strong: ({children}) => <>{children}</>,
  em: ({children}) => <>{children}</>,
};

// Renders text as bullet list (supports old array format and new markdown format)
const BulletListOrText = ({ value, highlightQuery }) => {
  if (!value) return null;

  // Handle old array format (backwards compatibility)
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.length === 1) {
      return highlightQuery ? highlightText(value[0], highlightQuery) : value[0];
    }
    return (
      <ul className="bullet-list">
        {value.map((item, idx) => (
          <li key={idx}>{highlightQuery ? highlightText(item, highlightQuery) : item}</li>
        ))}
      </ul>
    );
  }

  // Handle new markdown string format
  if (isMarkdownBullets(value)) {
    return <ReactMarkdown components={markdownComponents}>{value}</ReactMarkdown>;
  }

  // Plain string without bullets
  return highlightQuery ? highlightText(value, highlightQuery) : value;
};

// Helper to escape CSV values
const escapeCsvValue = (value) => {
  if (value == null) return '';
  const stringValue = String(value);
  // Escape double quotes by doubling them, and wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

// Helper to download CSV of all leads for a search
const downloadLeadsAsCSV = (search) => {
  const results = typeof search.results === 'string'
    ? JSON.parse(search.results)
    : search.results;

  if (!results || results.length === 0) {
    alert('No leads to download');
    return;
  }

  // CSV Headers
  const headers = [
    'Opportunity Name',
    'Fit Reason',
    'Founder Name',
    'Founder Background',
    'Founder LinkedIn',
    'Other Founders',
    'Reference'
  ];

  // Build CSV rows
  const rows = results.map(result => {
    const firstFounder = result.founders && result.founders.length > 0 ? result.founders[0] : null;
    const otherFounders = result.founders && result.founders.length > 1
      ? result.founders.slice(1).map(f => {
          const parts = [];
          if (f.name) parts.push(f.name);
          if (f.summary) parts.push(arrayToString(f.summary));
          if (isValidLinkedInUrl(f.linkedin)) parts.push(f.linkedin);
          return parts.join(' | ');
        }).join('; ')
      : '';

    const fitReason = arrayToString(result.fit_reason);
    const firstFounderSummary = arrayToString(firstFounder?.summary);

    return [
      escapeCsvValue(result.company_name || ''),
      escapeCsvValue(fitReason || ''),
      escapeCsvValue(firstFounder?.name || ''),
      escapeCsvValue(firstFounderSummary),
      escapeCsvValue(isValidLinkedInUrl(firstFounder?.linkedin) ? firstFounder.linkedin : ''),
      escapeCsvValue(otherFounders),
      escapeCsvValue(result.company_url || '')
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'cappuccino search results.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function DashboardSearches({ searches, refreshSearches, user }) {
  const [expandedSearchIds, setExpandedSearchIds] = useState(new Set());
  const [salesforceConnected, setSalesforceConnected] = useState(false);
  const [syncingLeads, setSyncingLeads] = useState({});
  const [syncedLeads, setSyncedLeads] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSearches, setFilteredSearches] = useState([]);
  const [updatingFeedback, setUpdatingFeedback] = useState({});
  const [retryingJobs, setRetryingJobs] = useState({});
  // Feedback modal state
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    resultId: null,
    companyName: '',
    feedbackType: null, // 'up' or 'down'
    existingFeedback: null // { id, feedback, details } - existing feedback record if any
  });

  // Check Salesforce connection status and fetch synced opportunities
  useEffect(() => {
    const checkSalesforceStatus = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('Users')
          .select('salesforce_connected')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setSalesforceConnected(data.salesforce_connected || false);

          // If connected, fetch existing synced opportunities from Salesforce
          if (data.salesforce_connected) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              try {
                const response = await fetch(`${BACKEND_URL}/salesforce/synced-opportunities`, {
                  headers: {
                    'Authorization': `Bearer ${session.access_token}`
                  }
                });
                if (response.ok) {
                  const result = await response.json();
                  // result.opportunities is { company_name_lowercase: opportunity_url }
                  setSyncedLeads(result.opportunities || {});
                }
              } catch (fetchErr) {
                console.error('Error fetching synced opportunities:', fetchErr);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking Salesforce status:', err);
      }
    };

    checkSalesforceStatus();
  }, [user]);

  // Helper to get feedback for a company from a search's feedback dict
  const getFeedbackForCompany = (search, companyName) => {
    if (!search.feedback) return null;
    return search.feedback[companyName] || null;
  };

  // Open feedback modal when clicking thumbs up/down
  const handleFeedbackClick = (search, companyName, feedbackType) => {
    const currentFeedback = getFeedbackForCompany(search, companyName);

    // If clicking the same feedback type, remove it (toggle off)
    if (currentFeedback && currentFeedback.feedback === feedbackType) {
      // Remove feedback directly without modal
      deleteFeedback(currentFeedback.id, companyName);
      return;
    }

    // Open modal for new or changed feedback
    setFeedbackModal({
      isOpen: true,
      resultId: search.result_id,
      companyName,
      feedbackType,
      existingFeedback: currentFeedback // null if new, { id, feedback, details } if existing
    });
  };

  // Close feedback modal
  const closeFeedbackModal = () => {
    setFeedbackModal({
      isOpen: false,
      resultId: null,
      companyName: '',
      feedbackType: null,
      existingFeedback: null
    });
  };

  // Create new feedback
  const createFeedback = async (resultId, companyName, feedbackType, details) => {
    const key = `${resultId}_${companyName}`;
    setUpdatingFeedback(prev => ({ ...prev, [key]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      await fetch(`${BACKEND_URL}/leads/create-lead-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          find_leads_result_id: resultId,
          company_name: companyName,
          feedback: feedbackType,
          details: details || null
        })
      });

      // Refresh searches to get updated feedback
      if (refreshSearches) {
        await refreshSearches();
      }
      closeFeedbackModal();
    } catch (err) {
      console.error('Error creating feedback:', err);
    } finally {
      setUpdatingFeedback(prev => ({ ...prev, [key]: false }));
    }
  };

  // Update existing feedback by ID
  const updateFeedback = async (feedbackId, feedbackType, details, companyName, resultId) => {
    const key = `${resultId}_${companyName}`;
    setUpdatingFeedback(prev => ({ ...prev, [key]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      await fetch(`${BACKEND_URL}/leads/update-lead-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: feedbackId,
          feedback: feedbackType,
          details: details || null
        })
      });

      // Refresh searches to get updated feedback
      if (refreshSearches) {
        await refreshSearches();
      }
      closeFeedbackModal();
    } catch (err) {
      console.error('Error updating feedback:', err);
    } finally {
      setUpdatingFeedback(prev => ({ ...prev, [key]: false }));
    }
  };

  // Delete feedback by ID
  const deleteFeedback = async (feedbackId, companyName) => {
    // Find the result_id for this feedback to use as key
    const search = searches.find(s => s.feedback && s.feedback[companyName]?.id === feedbackId);
    const key = search ? `${search.result_id}_${companyName}` : feedbackId;

    setUpdatingFeedback(prev => ({ ...prev, [key]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      await fetch(`${BACKEND_URL}/leads/delete-lead-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: feedbackId })
      });

      // Refresh searches to get updated feedback
      if (refreshSearches) {
        await refreshSearches();
      }
    } catch (err) {
      console.error('Error deleting feedback:', err);
    } finally {
      setUpdatingFeedback(prev => ({ ...prev, [key]: false }));
    }
  };

  // Handle modal submit
  const handleModalSubmit = (details) => {
    if (feedbackModal.existingFeedback) {
      // Update existing feedback
      updateFeedback(
        feedbackModal.existingFeedback.id,
        feedbackModal.feedbackType,
        details,
        feedbackModal.companyName,
        feedbackModal.resultId
      );
    } else {
      // Create new feedback
      createFeedback(
        feedbackModal.resultId,
        feedbackModal.companyName,
        feedbackModal.feedbackType,
        details
      );
    }
  };

  // Get results from the new data structure
  const getResultsForSearch = (search) => {
    // Results are now directly on the search object (flattened in Dashboard.js)
    let results = search.results;
    if (!results) return [];

    // Handle results that might be a JSON string
    if (typeof results === 'string') {
      try {
        results = JSON.parse(results);
      } catch (e) {
        console.error('Error parsing results:', e);
        return [];
      }
    }

    return Array.isArray(results) ? results : [];
  };

  // Filter searches based on search query and auto-expand matching ones
  useEffect(() => {
    if (!searches) {
      setFilteredSearches([]);
      return;
    }

    if (!searchQuery.trim()) {
      setFilteredSearches(searches);
      setExpandedSearchIds(new Set()); // Collapse all when query is cleared
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = [];
    const toExpand = new Set();

    searches.forEach(search => {
      // Search in the search query itself
      const searchQueryText = (search.query || '').toLowerCase();
      const matchesSearchQuery = searchQueryText.includes(query);

      // Search in results if available
      const results = getResultsForSearch(search);
      const matchesResults = results.some(result => {
        const companyName = (result.company_name || '').toLowerCase();
        const companyDesc = arrayToString(result.company_description).toLowerCase();
        const fitReason = arrayToString(result.fit_reason).toLowerCase();
        const founderNames = (result.founders || []).map(f => (f.name || '').toLowerCase()).join(' ');

        return companyName.includes(query) ||
          companyDesc.includes(query) ||
          fitReason.includes(query) ||
          founderNames.includes(query);
      });

      if (matchesSearchQuery || matchesResults) {
        filtered.push(search);
        // Auto-expand if there are matching results inside (not just the search query)
        if (matchesResults && (search.status === 'complete' || search.status === 'processing')) {
          toExpand.add(search.id);
        }
      }
    });

    setFilteredSearches(filtered);
    setExpandedSearchIds(toExpand);
  }, [searchQuery, searches]);

  // Retry a job
  const handleRetryJob = async (jobId, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (retryingJobs[jobId]) return;

    setRetryingJobs(prev => ({ ...prev, [jobId]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${BACKEND_URL}/leads/retry-job`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ job_id: jobId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to retry job');
      }

      // Refresh searches to show the new job, then reset state
      if (refreshSearches) {
        await refreshSearches();
      }
      setRetryingJobs(prev => ({ ...prev, [jobId]: false }));
    } catch (err) {
      console.error('Error retrying job:', err);
      alert(`Failed to retry: ${err.message}`);
      setRetryingJobs(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Sync lead to Salesforce
  const handleSyncToSalesforce = async (result, searchQueryParam) => {
    const companyKey = (result.company_name || '').toLowerCase();
    if (syncingLeads[companyKey] || syncedLeads[companyKey]) return;

    setSyncingLeads(prev => ({ ...prev, [companyKey]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${BACKEND_URL}/salesforce/sync-lead`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: result.company_name,
          company_description: result.company_description,
          founders: result.founders,
          fit_reason: result.fit_reason,
          search_query: searchQueryParam
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync');
      }

      const data = await response.json();
      setSyncedLeads(prev => ({ ...prev, [companyKey]: data.opportunity_url }));
    } catch (err) {
      console.error('Error syncing to Salesforce:', err);
      alert(`Failed to sync: ${err.message}`);
    } finally {
      setSyncingLeads(prev => ({ ...prev, [companyKey]: false }));
    }
  };

  const toggleSearch = async (search) => {
    // Allow expanding for both processing and complete searches
    if (search.status !== 'complete' && search.status !== 'processing') return;

    setExpandedSearchIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(search.id)) {
        newSet.delete(search.id);
      } else {
        newSet.add(search.id);
      }
      return newSet;
    });
  };

  if (!searches || searches.length === 0) {
    return (
      <div className="dashboard-searches">
        <h1 className="searches-title">Searches</h1>
        <div className="searches-empty">
          <p>No searches yet. Create your first search to get started!</p>
        </div>
      </div>
    );
  }

  const renderExpandedContent = (search) => {
    if (search.status === 'processing') {
      return (
        <div className="search-processing">
          <div className="processing-message">Searching for leads...</div>
        </div>
      );
    }

    const query = searchQuery.trim();

    return (
      <div className="people-results">
        {getResultsForSearch(search).length === 0 ? (
          <div className="no-people">No results found for this search</div>
        ) : (
          <div className="people-grid">
            {getResultsForSearch(search).map((result, index) => (
              <div key={result.person_id || index} className="person-card">
                <div className="person-header">
                  <div className="person-info">
                    <div className="person-name-row">
                      <h4 className="person-name">
                        {query ? highlightText(result.company_name || 'Unknown Company', query) : (result.company_name || 'Unknown Company')}
                      </h4>
                      <div className="person-actions">
                        {isValidUrl(result.company_url) && (
                          <a
                            href={result.company_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="company-link-icon"
                            title="Visit company website"
                          >
                            <LuExternalLink size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                    {result.company_description && (
                      <div className="person-title-company">
                        <BulletListOrText value={result.company_description} highlightQuery={query} />
                      </div>
                    )}
                  </div>
                </div>
                {result.founders && result.founders.length > 0 && (
                  <div className="founders-section">
                    <strong>Founders:</strong>
                    <div className="founders-list">
                      {result.founders.map((founder, idx) => (
                        <div key={idx} className="founder-item">
                          <div className="founder-name-row">
                            <span className="founder-name">
                              {query ? highlightText(founder.name, query) : founder.name}
                            </span>
                            {isValidLinkedInUrl(founder.linkedin) && (
                              <a
                                href={founder.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="founder-linkedin"
                                title={`${founder.name}'s LinkedIn`}
                              >
                                <FaLinkedin size={16} color="#0077b5" />
                              </a>
                            )}
                          </div>
                          {founder.summary && (
                            <div className="founder-summary">
                              <BulletListOrText value={founder.summary} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.fit_reason && (
                  <div className="person-detail">
                    <strong>Fit Reason:</strong>
                    <BulletListOrText value={result.fit_reason} highlightQuery={query} />
                  </div>
                )}
                {salesforceConnected && (
                  <div className="person-salesforce">
                    {(() => {
                      const companyKey = (result.company_name || '').toLowerCase();
                      if (syncedLeads[companyKey]) {
                        return (
                          <a
                            href={syncedLeads[companyKey]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="salesforce-synced-link"
                          >
                            <SiSalesforce size={14} />
                            View in Salesforce
                          </a>
                        );
                      }
                      return (
                        <button
                          className={`salesforce-sync-btn ${syncingLeads[companyKey] ? 'syncing' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSyncToSalesforce(result, search.query);
                          }}
                          disabled={syncingLeads[companyKey]}
                        >
                          <SiSalesforce size={14} />
                          {syncingLeads[companyKey] ? 'Syncing...' : 'Sync with Salesforce'}
                        </button>
                      );
                    })()}
                  </div>
                )}
                <div className="lead-feedback">
                  <span className="feedback-label">Is this a good lead?</span>
                  {(() => {
                    const feedbackKey = `${search.result_id}_${result.company_name}`;
                    const currentFeedback = getFeedbackForCompany(search, result.company_name);
                    const isUpdating = updatingFeedback[feedbackKey];

                    return (
                      <>
                        <button
                          className={`feedback-btn thumbs-up ${currentFeedback?.feedback === 'up' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFeedbackClick(search, result.company_name, 'up');
                          }}
                          disabled={isUpdating}
                          title="Good lead"
                        >
                          <FaThumbsUp size={14} />
                        </button>
                        <button
                          className={`feedback-btn thumbs-down ${currentFeedback?.feedback === 'down' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFeedbackClick(search, result.company_name, 'down');
                          }}
                          disabled={isUpdating}
                          title="Not a good lead"
                        >
                          <FaThumbsDown size={14} />
                        </button>
                        {currentFeedback?.details && (
                          <span
                            className="feedback-details-indicator"
                            title="Click to edit feedback"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFeedbackModal({
                                isOpen: true,
                                resultId: search.result_id,
                                companyName: result.company_name,
                                feedbackType: currentFeedback.feedback,
                                existingFeedback: currentFeedback
                              });
                            }}
                          >
                            <FaComment size={12} />
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-searches">
      <div className="searches-header">
        <h1 className="searches-title">Searches</h1>
        <input
          type="text"
          className="searches-search"
          placeholder="Search by company, founder, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Desktop table view */}
      <div className="searches-table-container">
        <table className="searches-table">
          <thead>
            <tr>
              <th>Search</th>
              <th>Status</th>
              <th>Created</th>
              <th>Results</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSearches.map((search) => (
              <>
                <tr
                  key={search.id}
                  onClick={() => toggleSearch(search)}
                  className={`${search.status === 'complete' || search.status === 'processing' ? 'clickable' : ''} ${expandedSearchIds.has(search.id) ? 'expanded' : ''}`}
                >
                  <td className="search-query-cell">
                    {(search.status === 'complete' || search.status === 'processing') && (
                      <span className="expand-icon">{expandedSearchIds.has(search.id) ? '▼' : '▶'}</span>
                    )}
                    {searchQuery.trim() ? highlightText(search.query, searchQuery.trim()) : search.query}
                  </td>
                  <td>
                    <span className={`search-status-badge status-${search.status}`}>
                      {search.status === 'failed' ? 'Please Retry' : search.status === 'processing' ? 'Brewing' : search.status}
                    </span>
                  </td>
                  <td className="search-date-cell">
                    {new Date(search.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="search-results-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>
                        {search.status === 'complete'
                          ? (getResultsForSearch(search).length || '-')
                          : search.status === 'failed'
                            ? '-'
                            : ''}
                      </span>
                      {search.status === 'complete' && getResultsForSearch(search).length > 0 && (
                        <button
                          className="download-csv-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadLeadsAsCSV(search);
                          }}
                          title="Download all leads as CSV"
                        >
                          <RiDownloadLine size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="search-actions-cell">
                    {search.status !== 'queued' && search.status !== 'processing' && (
                      <span
                        className={`rerun-link ${retryingJobs[search.id] ? 'disabled' : ''}`}
                        onClick={(e) => handleRetryJob(search.id, e)}
                      >
                        {retryingJobs[search.id] ? 'Re-running...' : 'Re-run search'}
                      </span>
                    )}
                  </td>
                </tr>
                {expandedSearchIds.has(search.id) && (
                  <tr key={`${search.id}-expanded`} className="expanded-row">
                    <td colSpan="5">
                      {renderExpandedContent(search)}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="searches-cards-container">
        {filteredSearches.map((search) => (
          <div
            key={search.id}
            className={`search-card ${search.status === 'complete' || search.status === 'processing' ? 'clickable' : ''} ${expandedSearchIds.has(search.id) ? 'expanded' : ''}`}
            onClick={() => toggleSearch(search)}
          >
            <div className="search-card-header">
              <div className="search-card-query">
                {(search.status === 'complete' || search.status === 'processing') && (
                  <span className="expand-icon">{expandedSearchIds.has(search.id) ? '▼' : '▶'}</span>
                )}
                {searchQuery.trim() ? highlightText(search.query, searchQuery.trim()) : search.query}
              </div>
              <span className={`search-status-badge status-${search.status}`}>
                {search.status === 'failed' ? 'Please Try Again' : search.status === 'processing' ? 'Brewing' : search.status}
              </span>
            </div>
            <div className="search-card-meta">
              <span className="search-card-date">
                {new Date(search.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
              <span className="search-card-results">
                {search.status === 'complete'
                  ? `${getResultsForSearch(search).length || 0} results`
                  : search.status === 'failed'
                    ? 'Failed'
                    : ''}
              </span>
              {search.status !== 'queued' && search.status !== 'processing' && (
                <span
                  className={`rerun-link ${retryingJobs[search.id] ? 'disabled' : ''}`}
                  onClick={(e) => handleRetryJob(search.id, e)}
                >
                  {retryingJobs[search.id] ? 'Re-running...' : 'Re-run search'}
                </span>
              )}
            </div>
            {expandedSearchIds.has(search.id) && (
              <div className="search-card-expanded" onClick={(e) => e.stopPropagation()}>
                {renderExpandedContent(search)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleModalSubmit}
        feedbackType={feedbackModal.feedbackType === 'up' ? 'thumbs-up' : 'thumbs-down'}
        companyName={feedbackModal.companyName}
        isSubmitting={updatingFeedback[`${feedbackModal.resultId}_${feedbackModal.companyName}`] || false}
        existingDetails={feedbackModal.existingFeedback?.details || ''}
      />
    </div>
  );
}

export default DashboardSearches;
